import { memo, useMemo } from "react";

import { parseGid, useProduct } from "@shopify/hydrogen-react";

import { ProductIntroView } from "@react/sections/ProductHero/ProductIntro/ProductIntro.view";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import {
  isStandaloneBundleKit,
  isProductFixedBundle,
  isVariantFixedBundle,
  isMigratedBundle,
} from "@react/utilities";
import { type ProductHeroSettings } from "@root/tablescape/state";
import {
  formatProduct,
  formatProductVariant,
} from "@root/tablescape/utilities";

const ProductIntroBase = () => {
  const { product, selectedVariant } = useProduct();

  const {
    productIntro: { description, reviewProductsOverride, showReviews, title },
    text: { bundleValueText },
  } = useSectionSettings<ProductHeroSettings>();

  const formattedProduct = useMemo(() => formatProduct(product), [product]);

  const formattedSelectedVariant = useMemo(
    () => formatProductVariant(selectedVariant),
    [selectedVariant]
  );

  const reviewProducts = useMemo(() => {
    if (reviewProductsOverride.length) {
      return reviewProductsOverride.map(({ handle, id, name }) => ({
        id,
        name,
        handle,
      }));
    } else if (isMigratedBundle(formattedProduct)) {
      return formattedProduct.productFixedBundleComponents?.map((product) => ({
        id: parseGid(product.id).id,
        name: product.title,
        handle: product.handle,
      }));
    } else if (
      isVariantFixedBundle([formattedSelectedVariant]) ||
      isStandaloneBundleKit(formattedSelectedVariant)
    ) {
      return formattedSelectedVariant.variantFixedBundleComponents?.map(
        (variant) => ({
          id: parseGid(variant.product.id).id,
          name: variant.product.title,
          handle: variant.product.handle,
        })
      );
    }

    return [
      {
        id: parseGid(product.id).id,
        name: product.title,
        handle: product.handle,
      },
    ];
  }, [
    product,
    formattedProduct,
    formattedSelectedVariant,
    reviewProductsOverride,
  ]);

  const reviewsSectionId = useMemo(
    () => `reviews-for-${parseGid(product.id).id}`,
    [product]
  );

  const badgeType = useMemo(() => {
    if (formattedSelectedVariant.priceBadgeText === "limited inventory") {
      return "senary";
    }

    if (isProductFixedBundle(formattedProduct)) {
      return "primary";
    }

    return "secondary";
  }, [formattedProduct, formattedSelectedVariant]);

  return (
    <ProductIntroView
      badgeText={formattedSelectedVariant.priceBadgeText}
      badgeType={badgeType}
      bundleValueText={bundleValueText}
      compareAtPrice={formattedSelectedVariant.compareAtPrice}
      compareAtPriceDisplay={formattedSelectedVariant.compareAtPriceDisplay}
      description={description}
      price={formattedSelectedVariant.price}
      reviewProducts={reviewProducts}
      reviewsSectionId={reviewsSectionId}
      setSize={formattedProduct.setSize}
      showReviews={showReviews}
      title={title}
    />
  );
};

export const ProductIntro = memo(ProductIntroBase);
